import { useC2pa } from "@contentauth/react";
import { Box, Link, Typography } from "@mui/material";
import { L2ManifestStore, createL2ManifestStore, generateVerifyUrl } from "c2pa";
import { ManifestSummary } from "c2pa-wc/dist/components/ManifestSummary";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider } from "rsuite";
import C2PAIcon from "../../../assets/images/icons/C2PAIcon";
import Calendar from "../../../assets/images/icons/Calendar";
import Certificate from "../../../assets/images/icons/Certificate";
import CloseIcon from "../../../assets/images/icons/CloseIcon";
import BriaButton from "../BriaButton/BriaButton";
import BriaIconButton from "../BriaIconButton/BriaIconButton";
import styles from "./C2PAButton.module.scss";

interface IProps {
	imageUrl: string;
	className?: string;
	isHovered?: boolean;
	btnClassName?: string;
}

function C2PAButton({ imageUrl, className, isHovered, btnClassName }: IProps) {
	const { t } = useTranslation("translation", { keyPrefix: "c2paPupup" });
	const provenance = useC2pa(imageUrl);
	const viewMoreUrl = generateVerifyUrl(imageUrl);
	const [isOpen, setIsOpen] = useState(false);
	const [date, setDate] = useState<string>();
	const [manifestStore, setManifestStore] = useState<L2ManifestStore | null>(null);
	const summaryRef = useRef<ManifestSummary>();
	const wrapperRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		let disposeFn = () => {};
		if (!provenance?.manifestStore?.activeManifest) return;

		createL2ManifestStore(provenance.manifestStore).then(({ manifestStore, dispose }) => {
			setManifestStore(manifestStore);
			const isoDateString = manifestStore?.signature?.isoDateString;
			if (isoDateString) {
				const date = new Date(isoDateString);
				setDate(
					date.toLocaleDateString("en-US", {
						month: "short",
						day: "numeric",
						year: "numeric",
					}),
				);
			}
			disposeFn = dispose;
		});

		return disposeFn;
	}, [provenance?.manifestStore?.activeManifest]);

	useEffect(() => {
		const summaryElement = summaryRef.current;
		if (summaryElement && manifestStore) {
			summaryElement.manifestStore = manifestStore;
			summaryElement.viewMoreUrl = viewMoreUrl;
		}
	}, [summaryRef, manifestStore]);

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		}

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen]);

	useEffect(() => {
		!isHovered && setIsOpen(false);
	}, [isHovered]);

	if (!provenance?.manifestStore?.activeManifest) {
		return;
	}

	return (
		<Box ref={wrapperRef} className={clsx(styles.c2paButton, className)}>
			<BriaButton
				buttonType="tertiaryMedium"
				onClick={() => setIsOpen(!isOpen)}
				className={clsx(styles.c2paIcon, btnClassName)}
			>
				<C2PAIcon />
			</BriaButton>
			{isOpen && (
				<Box className={styles.c2paContent}>
					<BriaIconButton className={styles.closeBtn} onClick={() => setIsOpen(false)}>
						<CloseIcon />
					</BriaIconButton>
					<Typography className={styles.title}>{t("title")}</Typography>
					<Typography className={styles.subTitle}>
						{t("description")}
						<Link onClick={() => window.open(viewMoreUrl, "_blank")} className={styles.link}>
							More details
						</Link>
					</Typography>
					<Divider className={styles.divider} />
					<Box className={styles.c2paManifest}>
						<Box className={styles.contentWrapper}>
							<Certificate className={styles.certificateIcon} />
							<Typography>
								{t("issuedBy")} {manifestStore?.signature?.issuer}
							</Typography>
						</Box>
						<Box className={styles.contentWrapper}>
							<Calendar className={styles.calendarIcon} />
							<Typography>
								{date && (
									<Typography>
										{t("issuedDate")} {date}
									</Typography>
								)}
							</Typography>
						</Box>
					</Box>
				</Box>
			)}
		</Box>
	);
}

export default C2PAButton;
